import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { Pencil } from "lucide-react";

import { ConversationPhase } from "shared/types";
import { messagesVar, noticeboardDataVar } from "@/variables/globalVar";
import { EventTypes } from "@/types/events";

import { useIsReportLocked, useReiterationExpert } from "shared/hooks";
import { useCreateConversationMessage } from "shared/apiHooks";
import { useAccount, useCaptureEvent, CaptureEventType } from "@/hooks";

import { Button } from "shared/components";

const AdjustProposalDialog = lazy(
  () => import("@/components/adjustProposal/AdjustProposalDialog"),
);

export interface EditProposalButtonProps {
  isPublic?: boolean;
}

export function EditProposalButton({ isPublic }: EditProposalButtonProps) {
  const { captureEvent } = useCaptureEvent();

  const [adjustProposalDialogOpen, setAdjustProposalDialogOpen] =
    useState(false);

  const { conversation } = useAccount();
  const messages = useAtomValue(messagesVar);

  const isReportLocked = useIsReportLocked({ conversation, messages });

  const noticeboardData = useAtomValue(noticeboardDataVar);
  const reiteration = useReiterationExpert(noticeboardData);

  const filteredReiteration = useMemo(() => {
    return reiteration?.replace(/^<.*>/, "").replace(/^<\/.*>/, "");
  }, [reiteration]);

  useEffect(() => {
    const handleAdjustPlansEvent: EventListener = (_event) => {
      setAdjustProposalDialogOpen(true);
    };
    document.addEventListener(
      EventTypes.AdjustPlans,
      handleAdjustPlansEvent,
    );

    return () => {
      document.removeEventListener(
        EventTypes.AdjustPlans,
        handleAdjustPlansEvent,
      );
    };
  }, []);

  useEffect(() => {
    if (adjustProposalDialogOpen) {
      captureEvent(CaptureEventType.USER_ADJUST_PROPOSAL_RENDERED);
    }
  }, [adjustProposalDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    mutate: createMessage,
    isError: isCreateMessageError,
    isPending: isCreateMessagePending,
  } = useCreateConversationMessage({
    onError: (error) => {
      console.error(
        "<ManageReport> useCreateConversationMessage() onError()",
        error,
      );
      setAdjustProposalDialogOpen(false);
    },
    onSuccess: () => {
      captureEvent(CaptureEventType.USER_ADJUSTED_PROPOSAL);
      setAdjustProposalDialogOpen(false);
    },
  });

  const handleClickAdjustProposal = () => {
    setAdjustProposalDialogOpen(true);
  };

  const handleSubmitAdjustProposal = (text: string) => {
    createMessage({
      body: {
        text,
        message_type: "AMENDMENT",
      },
      path: {
        application_id: conversation?.application || "",
        conversation_id: conversation?.id || "",
      },
    });
  };

  return (
    <>
      {!isPublic &&
      conversation?.phase === ConversationPhase.report_ready &&
      !isReportLocked ? (
        <Button onClick={handleClickAdjustProposal} variant="link">
          <Pencil className="max-h-3 max-w-3" /> Edit
        </Button>
      ) : null}
      <Suspense>
        <AdjustProposalDialog
          content={filteredReiteration}
          onSubmit={handleSubmitAdjustProposal}
          open={adjustProposalDialogOpen}
          onOpenChange={(open) => setAdjustProposalDialogOpen(open)}
          submitting={isCreateMessagePending}
        />
      </Suspense>
    </>
  );
}
